"use client";

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/lib/useReducedMotion";
import type { Testimonial } from "@/lib/types";
import { RevealGroup } from "@/components/ui/Reveal";
import { TestimonialCard } from "@/components/sections/TestimonialCard";

const CARD = 340; // px, fixed card width
const GAP = 16; // px, matches gap-4
const SPEED = 0.035; // px per ms

/** Student reviews drifting sideways in an endless loop; hover or touch pauses it so a video can be watched. */
export function ReviewsCarousel({ reviews }: { reviews: Testimonial[] }) {
  const reduceMotion = useReducedMotion();
  // Looping only starts with 3+ reviews; fewer are shown still.
  const reduce = reduceMotion || reviews.length < 3;
  const viewport = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const paused = useRef(false);
  const offset = useRef(0);
  const [copies, setCopies] = useState(2);
  const unit = reviews.length * (CARD + GAP); // width of one full copy of the list

  // enough copies that the strip always overflows the viewport and wraps seamlessly
  useEffect(() => {
    const el = viewport.current;
    if (!el || reduce) return;
    const measure = () => setCopies(Math.ceil(el.clientWidth / unit) + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [unit, reduce]);

  useEffect(() => {
    const t = track.current;
    if (!t || reduce) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(now - last, 64);
      last = now;
      if (!paused.current) {
        offset.current = (offset.current + dt * SPEED) % unit;
        t.style.transform = `translate3d(${-offset.current}px,0,0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [unit, reduce]);

  const list = reduce ? reviews.map((t) => ({ t, key: String(t.id) })) : Array.from({ length: copies }).flatMap((_, c) => reviews.map((t) => ({ t, key: `${c}-${t.id}` })));

  return (
    <RevealGroup>
      <div
        ref={viewport}
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => (paused.current = false)}
        onTouchStart={() => (paused.current = true)}
        onTouchEnd={() => (paused.current = false)}
        className={`mt-5 pb-2 ${reduce ? "overflow-x-auto" : "overflow-hidden"}`}
      >
        <div ref={track} className="flex w-max gap-4 will-change-transform">
          {list.map(({ t, key }) => (
            <div key={key} className="shrink-0" style={{ width: CARD }}>
              <TestimonialCard testimonial={t} />
            </div>
          ))}
        </div>
      </div>
    </RevealGroup>
  );
}
